import React from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  CalendarOff,
  ClipboardX,
  ListChecks,
  Timer,
} from "lucide-react";
import { MetricCard } from "../shared";
import { TEAM_ICON_COLOR, TEAM_ICON_SIZE } from "./constants";
import type {
  TeamKpiComparisonByKey,
  TeamKpiMetricKey,
  TeamSegment,
  TeamSummary,
} from "./types";

const KPI_CONFIG: Array<{
  key: TeamKpiMetricKey;
  titleKey: string;
  titleFallback: string;
  icon: React.ReactNode;
  getValue: (summary: TeamSummary) => string;
}> = [
  {
    key: "tasksCompleted",
    titleKey: "analytics.team.kpi.tasksCompleted",
    titleFallback: "Tasks completed",
    icon: <ListChecks />,
    getValue: (summary: TeamSummary) => String(summary.tasksCompleted),
  },
  {
    key: "tasksCancelled",
    titleKey: "analytics.team.kpi.tasksCancelled",
    titleFallback: "Tasks cancelled",
    icon: <ClipboardX />,
    getValue: (summary: TeamSummary) => String(summary.tasksCancelled),
  },
  {
    key: "avgTimeOnTask",
    titleKey: "analytics.team.kpi.avgTimeOnTask",
    titleFallback: "Avg time on task",
    icon: <Timer />,
    getValue: (summary: TeamSummary) => summary.avgTimeOnTask,
  },
  {
    key: "delays",
    titleKey: "analytics.team.kpi.delays",
    titleFallback: "Delays",
    icon: <CalendarClock />,
    getValue: (summary: TeamSummary) => String(summary.delays),
  },
  {
    key: "daysOff",
    titleKey: "analytics.team.kpi.dayOff",
    titleFallback: "Day off",
    icon: <CalendarOff />,
    getValue: (summary: TeamSummary) => String(summary.daysOff),
  },
];

interface TeamKpiCardsProps {
  summary: TeamSummary;
  segment: TeamSegment;
  comparisonByKey?: TeamKpiComparisonByKey;
}

export const TeamKpiCards: React.FC<TeamKpiCardsProps> = ({
  summary,
  segment,
  comparisonByKey,
}) => {
  const { t } = useTranslation();
  const metrics = segment === "teams" ? KPI_CONFIG.slice(0, 3) : KPI_CONFIG;
  const columns = segment === "teams" ? 3 : 5;
  const gridClass =
    columns === 3
      ? "grid-cols-1 sm:grid-cols-3"
      : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-5";

  return (
    <div className={`grid w-full gap-3 ${gridClass}`}>
      {metrics.map((metric) => {
        const comparison = comparisonByKey?.[metric.key];

        return (
          <MetricCard
            key={metric.titleKey}
            title={t(metric.titleKey, metric.titleFallback)}
            value={metric.getValue(summary)}
            delta={comparison?.delta}
            trend={comparison?.trend}
            icon={metric.icon}
            iconColor={TEAM_ICON_COLOR}
            iconSize={TEAM_ICON_SIZE}
            width="fill"
          />
        );
      })}
    </div>
  );
};
